import { createClient } from '@supabase/supabase-js';
import { API_URL } from './config/api';

let clientInstance = null;
let configPromise = null;

const envUrl = process.env.REACT_APP_SUPABASE_URL;
const envAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Check if credentials are directly injected via environment
export const isConfiguredInEnv = Boolean(envUrl && envAnonKey && envUrl.trim() !== '' && envAnonKey.trim() !== '');
if (isConfiguredInEnv) {
  clientInstance = createClient(envUrl, envAnonKey, {
    auth: {
      flowType: 'pkce',
      detectSessionInUrl: true,
      persistSession: true,
      autoRefreshToken: true,
    },
  });
}

/**
 * Loads Supabase configuration from process.env, or fetches keys dynamically
 * from the backend (which reads from AWS SSM Parameter Store).
 */
export async function getSupabaseConfig() {
  if (clientInstance) {
    return {
      supabaseUrl: envUrl,
      supabaseAnonKey: envAnonKey,
      client: clientInstance,
    };
  }

  if (!configPromise) {
    configPromise = fetch(`${API_URL}/auth/config`)
      .then((res) => {
        if (!res.ok) throw new Error(`Server returned HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const { supabaseUrl, supabaseAnonKey } = data;
        if (!supabaseUrl || !supabaseAnonKey) {
          throw new Error('Supabase URL or Anon Key is missing from AWS SSM / server configuration.');
        }
        clientInstance = createClient(supabaseUrl, supabaseAnonKey, {
          auth: {
            flowType: 'pkce',
            detectSessionInUrl: true,
            persistSession: true,
            autoRefreshToken: true,
          },
        });

        return { supabaseUrl, supabaseAnonKey, client: clientInstance };
      })
      .catch((err) => {
        configPromise = null;
        console.warn('Could not load Supabase credentials from AWS/server:', err.message);
        throw err;
      });
  }

  return configPromise;
}

/**
 * Returns an initialized Supabase client, fetching keys from AWS if needed.
 */
export async function getSupabaseClient() {
  if (clientInstance) return clientInstance;
  const { client } = await getSupabaseConfig();
  return client;
}

// Transparent proxy for backward compatibility and synchronous imports
export const supabase = new Proxy(
  {},
  {
    get(_target, prop) {
      if (clientInstance) {
        return clientInstance[prop];
      }
      if (prop === 'auth') {
        return new Proxy(
          {},
          {
            get(_authTarget, authProp) {
              if (clientInstance) {
                return clientInstance.auth[authProp];
              }
              return async (...args) => {
                const client = await getSupabaseClient();
                return client.auth[authProp](...args);
              };
            },
          }
        );
      }
      return undefined;
    },
  }
);